<template>
    <div>
        <div v-if="!fetchingPoolInvitations">
            <div class="d-flex flex-wrap align-center">
                <div v-if="isResourcePoolManager">
                    <ResourcePoolInviteDialog @invited="fetchPoolInvitations">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" v-bind="attrs" v-on="on">
                                <v-icon left small>add</v-icon>
                                Invite
                            </v-btn>
                        </template>
                    </ResourcePoolInviteDialog>
                </div>
                <v-spacer />
                <v-text-field
                    v-model="invitationEmailSearch"
                    autofocus
                    placeholder="Search for an invitation..."
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    hide-details>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch inset dense hide-details class="pa-0 ma-0" v-model="invitationsSwitchData[invitationType]">
                                            <template v-slot:label>
                                                <div>
                                                    <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                    <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="ml-2" v-on="on" @click="fetchPoolInvitations" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>
            <v-data-table
                :headers="invitationHeaders"
                :items="filteredInvitationsList"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                </template>
                <template v-slot:[`item.invitation`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium">{{ item.requestee_email }}</div>
                        <div class="caption mt-1" v-if="item.notification_timestamp">
                            <span class="font-weight-bold">Sent on:</span>
                            {{ item.notification_timestamp | dateTimeToHuman }}
                        </div>
                        <div class="caption mt-1" v-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.REVOKED && item.revocation_timestamp">
                            <span class="font-weight-bold">Revoked on:</span>
                            {{ item.revocation_timestamp | dateTimeToHuman }}
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div v-for="(role, index) in item.roles" :key="index" class="d-flex flex-column">
                            <v-chip outlined small label class="mb-1 d-inline-block">
                                <v-icon left small>mdi-chart-box-outline</v-icon>
                                <span>{{ role.Name }}</span>
                            </v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        left
                        offset-x
                        nudge-top="10"
                        v-if="
                            isResourcePoolManager &&
                            (getInvitationStatus(item) === invitationAndRequestStatusTypes.PENDING ||
                                getInvitationStatus(item) === invitationAndRequestStatusTypes.CREATED)
                        ">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="fetchPoolInvitations"
                                :invitationInfo="item"
                                :actionType="actionTypes.RESEND_INVITATION"></InvitationActionsDialog>
                            <copy-to-clipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem"></copy-to-clipboard>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="fetchPoolInvitations"
                                :invitationInfo="item"
                                :actionType="actionTypes.REVOKE_INVITATION"></InvitationActionsDialog>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-email-off-outline</v-icon>
                        <span class="font-weight-medium secondary--text mb-1">No invitations found</span>
                        <span class="text-center">This resource pool has no invitation history yet.</span>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-magnify-close</v-icon>
                        <span>No invitations found for the current filters.</span>
                    </div>
                </template>
            </v-data-table>
        </div>
        <div v-else-if="fetchingPoolInvitations">
            <div
                v-for="item in [
                    { id: 'c1', opacity: 1 },
                    { id: 'c2', opacity: 0.75 },
                    { id: 'c3', opacity: 0.5 }
                ]"
                :key="item.id"
                class="my-6">
                <div :style="{ opacity: item.opacity }">
                    <v-skeleton-loader type="list-item-avatar-two-line" class="mx-auto" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { capitalize, sortArray } from '@/utils'
import ResourcePoolInviteDialog from '@/modules/user/components/ResourcePoolInviteDialog.vue'

const CopyToClipboard = () => import('@/components/CopyToClipboard')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog')

const createDefaultInvitationsSwitchData = () => ({
    ACCEPTED: true,
    PENDING: true,
    REJECTED: true,
    REVOKED: true,
    EXPIRED: true,
    BROKEN: true,
    CREATED: true
})

export default {
    name: 'PoolUserInvitations',
    mixins: [invitationsAndRequests],
    components: {
        CopyToClipboard,
        InvitationActionsDialog,
        ResourcePoolInviteDialog
    },
    data() {
        return {
            poolInvitations: [],
            fetchingPoolInvitations: false,
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: createDefaultInvitationsSwitchData(),
            invitationEmailSearch: ''
        }
    },
    computed: {
        ...mapGetters('userStore', ['isResourcePoolManager']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        activeResourcePoolId() {
            const routePoolId = Number(this.$route.params.rpid)
            if (Number.isFinite(routePoolId) && routePoolId > 0) {
                return routePoolId
            }

            const selectedPoolId = Number(this.selectedResourcePool)
            if (Number.isFinite(selectedPoolId) && selectedPoolId > 0) {
                return selectedPoolId
            }

            return Number(this.availableResourcePools[0]?.rpid || 0)
        },
        invitationHeaders() {
            const headers = [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Roles', value: 'roles', sortable: true }
            ]
            if (this.isResourcePoolManager) {
                headers.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '170px' })
            }
            return headers
        },
        availableInvitationTypes() {
            const invitationTypes = []
            if (this.poolInvitations && this.poolInvitations.length) {
                this.poolInvitations.forEach(invitation => {
                    const invitationType = this.getInvitationStatus(invitation)
                    if (!invitationTypes.includes(invitationType)) {
                        invitationTypes.push(invitationType)
                    }
                })
                return invitationTypes
            }
            return []
        },
        displayedInvitationTypes() {
            return Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
        },
        filteredInvitationsList() {
            const filterByStatusType = this.poolInvitations.filter(invitation => this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation)))
            if (!this.invitationEmailSearch) {
                return sortArray(filterByStatusType, 'invid', 'descending', false)
            }

            const filteredData = filterByStatusType.filter(invitation =>
                invitation.requestee_email.toLowerCase().includes(this.invitationEmailSearch.toLowerCase())
            )
            return sortArray(filteredData, 'invid', 'descending', false)
        }
    },
    methods: {
        capitalize,
        resetInvitationFilters() {
            this.invitationsSwitchData = createDefaultInvitationsSwitchData()
        },
        async fetchPoolInvitations() {
            if (!this.activeResourcePoolId) {
                this.poolInvitations = []
                return
            }

            this.resetInvitationFilters()
            this.fetchingPoolInvitations = true

            this.poolInvitations = []

            try {
                const { data } = await this.$axios.post(`/resources/${this.activeResourcePoolId}/invitations`, {})
                if (Array.isArray(data)) {
                    this.poolInvitations = data
                }
            } catch (error) {
                this.poolInvitations = []
            }

            this.fetchingPoolInvitations = false
        }
    },
    watch: {
        activeResourcePoolId: {
            handler() {
                this.fetchPoolInvitations()
            },
            immediate: true
        }
    }
}
</script>
